import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
import {
  LogOut,
  PanelLeft,
  HardHat,
  ChevronDown,
  KeyRound,
} from "lucide-react";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from './DashboardLayoutSkeleton';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import NotificationBell from "./NotificationBell";
import GlobalSearch from "./GlobalSearch";
import MobileBottomNav from "./MobileBottomNav";
import AdditionalTools from "./AdditionalTools";
import {
  getAdditionalToolGroups,
  getAllNavigationItems,
  getPersonalToolItems,
  getVisibleConciseMainItems,
  getVisibleLegacyGroups,
  getVisibleLegacyMainItems,
  isNavigationItemActive,
  legacyMenuGroups,
  ORIGINAL_NAVIGATION_KEY,
} from "./dashboardNavigation";
import { trpc } from "@/lib/trpc";

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const SIDEBAR_GROUPS_KEY = "sidebar-groups-open";
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;
const allMenuItems = getAllNavigationItems();

function LoginScreen() {
  const [mode, setMode] = useState<"choose" | "employee" | "forgot">("employee");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Check for auth_error from OAuth callback redirect
  const authErrorParam = new URLSearchParams(window.location.search).get("auth_error");
  const [error, setError] = useState(() => {
    if (authErrorParam === "callback_failed") return "Sign-in failed. Please try again.";
    if (authErrorParam === "missing_params") return "Invalid sign-in link. Please try again.";
    if (authErrorParam === "no_openid") return "Account info could not be retrieved. Please try again.";
    if (authErrorParam) return "Sign-in error. Please try again.";
    return "";
  });

  // Clean up the URL to remove auth_error param
  useEffect(() => {
    if (authErrorParam) {
      const url = new URL(window.location.href);
      url.searchParams.delete("auth_error");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [authErrorParam]);

  const forgotMutation = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: () => {
      setForgotSuccess(true);
      setIsLoading(false);
    },
    onError: (err) => {
      setError(err.message || "Failed to send reset request");
      setIsLoading(false);
    },
  });

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    forgotMutation.mutate({ email, origin: window.location.origin });
  };

  const loginMutation = trpc.auth.employeeLogin.useMutation({
    onSuccess: (data) => {
      // Store the JWT token in localStorage for reliable auth (bypasses cookie issues)
      if (data.token) {
        try {
          localStorage.setItem('darkhorse_employee_token', data.token);
        } catch (e) {
          console.warn('[Auth] Failed to save token to localStorage');
        }
      }
      // Reload to pick up the authenticated state
      window.location.reload();
    },
    onError: (err) => {
      setError(err.message || "Invalid email or password");
      setIsLoading(false);
    },
  });

  const handleEmployeeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center gap-6 p-8 max-w-md w-full">
        <div className="flex flex-col items-center gap-4">
          <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center">
            <HardHat className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-center text-foreground">
            DarkHorse Pro
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Sign in to access your construction management platform.
          </p>
        </div>

        {mode === "choose" && (
          <div className="flex flex-col gap-3 w-full">
            <Button
              onClick={() => setMode("employee")}
              size="lg"
              className="w-full shadow-lg hover:shadow-xl transition-all"
            >
              Sign in with Email & Password
            </Button>
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = getLoginUrl();
              }}
              size="lg"
              className="w-full"
            >
              Sign in with Manus Account
            </Button>
          </div>
        )}

        {mode === "employee" && (
          <form onSubmit={handleEmployeeLogin} className="flex flex-col gap-4 w-full">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm rounded-lg px-4 py-3">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="emp-email">Email</Label>
              <Input
                id="emp-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp-password">Password</Label>
              <Input
                id="emp-password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full shadow-lg hover:shadow-xl transition-all"
              disabled={isLoading}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => { setMode("forgot"); setError(""); setForgotSuccess(false); }}
                className="text-sm text-primary hover:underline"
              >
                Forgot Password?
              </button>
              <button
                type="button"
                onClick={() => { setMode("choose"); setError(""); }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Admin? Sign in with Manus
              </button>
            </div>
          </form>
        )}

        {mode === "forgot" && (
          <div className="flex flex-col gap-4 w-full">
            {forgotSuccess ? (
              <div className="text-center space-y-4">
                <div className="bg-green-500/10 text-green-600 rounded-lg px-4 py-3 text-sm">
                  If an account with that email exists, your admin has been notified and will send you a reset link.
                </div>
                <Button
                  variant="outline"
                  onClick={() => { setMode("employee"); setForgotSuccess(false); setError(""); }}
                  className="w-full"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground text-center">
                  Enter your email address and we'll notify your admin to send you a password reset link.
                </p>
                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Request Password Reset"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setMode("employee"); setError(""); }}
                  className="w-full text-muted-foreground"
                >
                  Back to Sign In
                </Button>
              </form>
            )}
          </div>
        )}


      </div>
    </div>
  );
}



export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  // Must call all hooks before any conditional returns (React rules of hooks)
  const { data: empStatus } = trpc.auth.isEmployee.useQuery(undefined, { enabled: !!user });
  const { data: notifCounts } = trpc.sidebarBadges.counts.useQuery(undefined, { enabled: !!user, refetchInterval: 60000 });
  const inboxApi = trpc;
  const { data: clientInboxUnreadCount = 0 } = inboxApi.clientInbox.unreadCount.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Map paths to notification counts for sidebar badges
  const badgeMap: Record<string, number> = useMemo(() => {
    const m: Record<string, number> = {};
    if (clientInboxUnreadCount > 0) m["/client-inbox"] = clientInboxUnreadCount;
    if (!notifCounts) return m;
    // Bills: show open/unpaid vendor bills count (+ bill imports needing review)
    const billsTotal = (notifCounts.openBills || 0) + (notifCounts.billsNeedingReview || 0);
    if (billsTotal > 0) { m["/bills"] = billsTotal; m["/bill-queue"] = notifCounts.billsNeedingReview || 0; }
    if (notifCounts.overdueInvoices > 0) m["/invoices"] = notifCounts.overdueInvoices;
    if (notifCounts.pendingChangeOrders > 0) m["/change-orders"] = notifCounts.pendingChangeOrders;
    if (notifCounts.expiredCoi > 0) m["/vendors"] = notifCounts.expiredCoi;
    if (notifCounts.pendingMatches > 0) m["/banking"] = notifCounts.pendingMatches;
    if (clientInboxUnreadCount > 0) m["/client-inbox"] = clientInboxUnreadCount;
    return m;
  }, [notifCounts, clientInboxUnreadCount]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />
  }

  if (!user) {
    return <LoginScreen />;
  }

  // Employee password change is now optional — handled via /change-password page or settings

  return (
    <SidebarProvider
      defaultOpen={false}
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth} badgeMap={badgeMap}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
  badgeMap: Record<string, number>;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
  badgeMap,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Role-based navigation filtering
  const userRole = user?.role || "user";
  const isCrewRole = userRole === "crew";
  const isSubRole = userRole === "subcontractor";
  const isSalesRole = userRole === "sales";
  const isRestricted = isCrewRole || isSubRole;
  const [showOriginalNavigation, setShowOriginalNavigation] = useState(() => {
    try {
      return typeof window !== "undefined" && localStorage.getItem(ORIGINAL_NAVIGATION_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Brand selector state
  const { data: brands } = trpc.brands.list.useQuery(undefined, { enabled: !!user });
  const [activeBrandId, setActiveBrandId] = useState<number | null>(() => {
    const saved = localStorage.getItem('darkhorse_active_brand');
    return saved ? Number(saved) : null;
  });

  // Feature toggles — controls which features appear in sidebar
  const { data: featureToggles } = trpc.featureToggles.getAll.useQuery(undefined, { enabled: !!user });

  // Map feature keys to sidebar paths that should be hidden when disabled
  const featurePathMap: Record<string, string[]> = {
    voice_transcripts: ["/voice-recordings"],
    ai_chat: ["/ai-chat"],
    action_queue: ["/action-queue"],
    daily_reports: ["/daily-reports", "/crew-reports"],
    ai_insights: ["/ai-insights"],
    intelligence_dashboard: ["/intelligence"],
    order_lists: ["/order-lists"],
    change_orders: ["/change-orders"],
    compliance: ["/compliance"],
    banking: ["/banking"],
    backup: ["/backup"],
    purchase_orders: ["/purchase-orders"],
    social_media: ["/social-media", "/social-accounts"],
    bid_board: ["/bid-management", "/bid-tracker"],
    receipts: ["/receipts", "/receipts/upload"],
    invoices: ["/invoices"],
    proposals: [],
    customers: ["/customers"],
    employees: ["/user-management"],
    subcontractors: ["/subcontractors"],
    ai_memory: ["/ai-memory"],
    stripe_dashboard: ["/stripe-dashboard"],
  };

  const hiddenPaths = useMemo(() => {
    if (!featureToggles) return new Set<string>();
    const paths = new Set<string>();
    for (const [key, enabled] of Object.entries(featureToggles)) {
      if (!enabled && featurePathMap[key]) {
        for (const p of featurePathMap[key]) paths.add(p);
      }
    }
    return paths;
  }, [featureToggles]);

  const legacyMainItems = useMemo(
    () => getVisibleLegacyMainItems(userRole, hiddenPaths),
    [userRole, hiddenPaths],
  );
  const legacyGroups = useMemo(
    () => getVisibleLegacyGroups(userRole, hiddenPaths),
    [userRole, hiddenPaths],
  );
  const conciseMainItems = useMemo(
    () => getVisibleConciseMainItems(userRole, hiddenPaths),
    [userRole, hiddenPaths],
  );
  const personalToolItems = useMemo(
    () => getPersonalToolItems(userRole, hiddenPaths),
    [userRole, hiddenPaths],
  );
  const additionalToolGroups = useMemo(
    () => getAdditionalToolGroups(userRole, hiddenPaths),
    [userRole, hiddenPaths],
  );
  const visibleMainItems = showOriginalNavigation ? legacyMainItems : conciseMainItems;
  const visibleGroups = showOriginalNavigation ? legacyGroups : [];

  const activeMenuItem = allMenuItems.find(item => item.path === location) || allMenuItems.find(item => location.startsWith(item.path) && item.path !== "/");

  // Collapsible group state
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_GROUPS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    // Default: core groups open, less-used groups collapsed
    const defaultOpen: Record<string, boolean> = {};
    for (const g of legacyMenuGroups) {
      // Pipeline, Money In, Expenses, Financial Reports default open; others collapsed
      defaultOpen[g.key] = ["pipeline", "money-in", "expenses", "financial-reports"].includes(g.key);
    }
    // Admin also collapsed by default
    defaultOpen["admin"] = false;
    return defaultOpen;
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  useEffect(() => {
    try {
      localStorage.setItem(ORIGINAL_NAVIGATION_KEY, String(showOriginalNavigation));
    } catch {
      // Navigation preference is optional; continue normally when storage is unavailable.
    }
  }, [showOriginalNavigation]);

  const toggleGroup = (key: string) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="justify-center py-3">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-sidebar-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4 text-sidebar-foreground/70" />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center gap-2 min-w-0">
                  <HardHat className="h-5 w-5 text-sidebar-primary shrink-0" />
                  <span className="font-semibold tracking-tight truncate text-sm text-sidebar-foreground">
                    DarkHorse Pro
                  </span>
                </div>
              ) : null}
            </div>
            {!isCollapsed && brands && (brands as any[]).length > 1 && (
              <div className="px-2 mt-2">
                <select
                  value={activeBrandId || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) {
                      localStorage.setItem('darkhorse_active_brand', val);
                      setActiveBrandId(Number(val));
                    } else {
                      localStorage.removeItem('darkhorse_active_brand');
                      setActiveBrandId(null);
                    }
                  }}
                  className="w-full text-xs rounded-md border border-sidebar-border bg-sidebar px-2 py-1.5 text-sidebar-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Brands</option>
                  {(brands as any[]).map((b: any) => (
                    <option key={b.id} value={b.id}>{b.code} - {b.name}</option>
                  ))}
                </select>
              </div>
            )}
          </SidebarHeader>

          <SidebarContent className="gap-0 overflow-y-auto overflow-x-hidden">
            {/* Concise workflow navigation; original mode renders the preserved legacy inventory. */}
            <SidebarMenu className="px-2 py-1 shrink-0">
              {visibleMainItems.map(item => {
                const isActive = isNavigationItemActive(item.path, location);
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      tooltip={item.label}
                      className="h-9 transition-all font-normal"
                    >
                      <item.icon
                        className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`}
                      />
                      <span className="flex-1">{item.label}</span>
                      {badgeMap[item.path] && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                          {badgeMap[item.path]}
                        </span>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>

            {!showOriginalNavigation && personalToolItems.length > 0 && (
              <div className="mt-1 shrink-0">
                {!isCollapsed && (
                  <div className="px-3 pt-3 pb-1">
                    <div className="mb-2 h-px bg-sidebar-border" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40">
                      Personal Tools
                    </span>
                  </div>
                )}
                <SidebarMenu className="px-2">
                  {personalToolItems.map(item => {
                    const isActive = isNavigationItemActive(item.path, location);
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          isActive={isActive}
                          onClick={() => setLocation(item.path)}
                          tooltip={item.label}
                          className="h-9 transition-all font-normal"
                        >
                          <item.icon className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`} />
                          <span className="flex-1">{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </div>
            )}

            {!showOriginalNavigation && (
              <AdditionalTools
                groups={additionalToolGroups}
                badgeMap={badgeMap}
                location={location}
                onNavigate={setLocation}
                isCompact={isCollapsed && !isMobile}
                onRequestExpanded={() => {
                  if (isCollapsed && !isMobile) toggleSidebar();
                }}
              />
            )}

            {/* Preserved original group rendering for one-click local rollback. */}
            {visibleGroups.map(group => {
              const isOpen = openGroups[group.key] !== false;
              const hasActiveItem = group.items.some(item => isNavigationItemActive(item.path, location));

              return (
                <div key={group.key} className="mt-1 shrink-0">
                  {/* Group header with separator */}
                  {!isCollapsed ? (
                    <div className="px-3 pt-3 pb-1">
                      <div className="h-px bg-sidebar-border mb-2" />
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className="flex items-center justify-between w-full text-left group/header hover:text-sidebar-foreground/70 transition-colors"
                      >
                        <span className={`text-[10px] uppercase tracking-widest font-semibold ${hasActiveItem ? "text-sidebar-primary" : "text-sidebar-foreground/40"}`}>
                          {group.label}
                        </span>
                        <ChevronDown
                          className={`h-3 w-3 text-sidebar-foreground/30 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`}
                        />
                      </button>
                    </div>
                  ) : (
                    <div className="h-2" />
                  )}

                  {/* Group items */}
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      !isCollapsed && !isOpen ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
                    }`}
                  >
                    <SidebarMenu className="px-2">
                      {group.items.map(item => {
                        const isActive = isNavigationItemActive(item.path, location);
                        return (
                          <SidebarMenuItem key={item.path}>
                            <SidebarMenuButton
                              isActive={isActive}
                              onClick={() => setLocation(item.path)}
                              tooltip={item.label}
                              className="h-9 transition-all font-normal"
                            >
                              <item.icon
                                className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`}
                              />
                              <span className="flex-1">{item.label}</span>
                              {badgeMap[item.path] && (
                                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                                  {badgeMap[item.path]}
                                </span>
                              )}
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </div>
                </div>
              );
            })}

            {!isCollapsed && (
              <div className="mt-auto px-3 py-3">
                <div className="mb-2 h-px bg-sidebar-border" />
                <label className="flex cursor-pointer items-center justify-between gap-3 text-xs text-sidebar-foreground/70">
                  <span>Show original navigation</span>
                  <Switch
                    checked={showOriginalNavigation}
                    onCheckedChange={setShowOriginalNavigation}
                    aria-label="Show original navigation"
                  />
                </label>
              </div>
            )}

            {/* Bottom spacer for scroll */}
            <div className="h-4" />
          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-sidebar-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border border-sidebar-border shrink-0">
                    <AvatarFallback className="text-xs font-medium bg-sidebar-primary/20 text-sidebar-primary">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none text-sidebar-foreground">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-sidebar-foreground/60 truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                    {(isRestricted || isSalesRole) && (
                      <span className="inline-block text-[10px] uppercase tracking-wider font-semibold mt-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                        {userRole}
                      </span>
                    )}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => setLocation("/change-password")}
                  className="cursor-pointer"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  <span>Change Password</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <span className="tracking-tight text-foreground">
                    {activeMenuItem?.label ?? "Menu"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <GlobalSearch />
              <NotificationBell />
            </div>
          </div>
        )}
        {!isMobile && (
          <div className="flex border-b h-12 items-center justify-end bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <GlobalSearch />
              <NotificationBell />
            </div>
          </div>
        )}
        <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6">{children}</main>
        <MobileBottomNav />
      </SidebarInset>
    </>
  );
}
